<div class="space-y-6 animate-in fade-in duration-500">

  <!-- Header -->
  <div class="flex items-center justify-between">
    <div class="space-y-0.5">
      <h2 class="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">Scheduled Agents</h2>
      <p class="text-sm text-gray-500 dark:text-gray-400">
        Monitor cron schedules, run history, and per-agent execution details.
      </p>
    </div>
    <button
      type="button"
      (click)="refresh()"
      class="flex items-center gap-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-white transition-colors px-3 py-1.5 rounded-lg hover:bg-indigo-50 dark:hover:bg-white/5"
    >
      <app-icon-v2 name="refresh-ccw" [size]="13" [class]="(schedulesLoading() || logsLoading()) ? 'animate-spin' : ''"></app-icon-v2>
      Refresh
    </button>
  </div>

  <!-- Stats row -->
  <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
    <div class="rounded-xl border border-indigo-100 dark:border-indigo-500/20 bg-gradient-to-br from-indigo-50/50 to-white dark:from-gray-900/80 dark:to-indigo-950/20 p-4 shadow-sm">
      <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Active Schedules</p>
      <p class="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{{ activeSchedulesCount() }}</p>
      <p class="text-xs text-gray-400 dark:text-gray-500">/ {{ schedules().length }} total</p>
    </div>
    <div class="rounded-xl border border-emerald-100 dark:border-emerald-500/20 bg-gradient-to-br from-emerald-50/50 to-white dark:from-gray-900/80 dark:to-emerald-950/20 p-4 shadow-sm">
      <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Runs Today</p>
      <p class="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{{ totalRunsToday() }}</p>
    </div>
    <div class="rounded-xl border border-amber-100 dark:border-amber-500/20 bg-gradient-to-br from-amber-50/50 to-white dark:from-gray-900/80 dark:to-amber-950/20 p-4 shadow-sm">
      <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Success Rate</p>
      <p class="text-2xl font-bold text-amber-600 dark:text-amber-400">
        @if (successRateToday() !== null) { {{ successRateToday() }}% } @else { — }
      </p>
    </div>
      <div class="rounded-xl border border-purple-100 dark:border-purple-500/20 bg-gradient-to-br from-purple-50/50 to-white dark:from-gray-900/80 dark:to-purple-950/20 p-4 shadow-sm">
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Tokens Today</p>
        <p class="text-2xl font-bold text-purple-600 dark:text-purple-400">{{ formatTokens(totalTokensToday()) }}</p>
        <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">Page tokens {{ formatTokens(logsPageTokensUsed()) }}</p>
      </div>
  </div>

  <!-- Tab bar -->
  <div class="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700/60">
    <button
      (click)="setTab('schedules')"
      [class]="activeTab() === 'schedules'
        ? 'border-b-2 border-indigo-600 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400 font-semibold'
        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'"
      class="px-4 py-2.5 text-sm transition-colors -mb-px"
    >
      Agents
    </button>
    <button
      (click)="setTab('logs')"
      [class]="activeTab() === 'logs'
        ? 'border-b-2 border-indigo-600 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400 font-semibold'
        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'"
      class="px-4 py-2.5 text-sm transition-colors -mb-px"
    >
      Logs
    </button>
    <button
      (click)="setTab('delivery')"
      [class]="activeTab() === 'delivery'
        ? 'border-b-2 border-indigo-600 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400 font-semibold'
        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'"
      class="px-4 py-2.5 text-sm transition-colors -mb-px"
    >
      Delivery Settings
    </button>
    @if (activeTab() === 'dag') {
      <button
        [class]="'border-b-2 border-indigo-600 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400 font-semibold px-4 py-2.5 text-sm -mb-px'"
      >
        Agent Breakdown
      </button>
    }
  </div>

  <!-- ── SCHEDULES TAB ── -->
  @if (activeTab() === 'schedules') {
    @if (schedulesLoading() && !schedules().length) {
      <div class="flex items-center justify-center py-16 text-gray-400 dark:text-gray-600">
        <app-icon-v2 name="refresh-ccw" [size]="20" class="animate-spin mr-2"></app-icon-v2>
        Loading schedules…
      </div>
    } @else if (!schedules().length) {
      <div class="flex flex-col items-center justify-center py-16 gap-3 text-center">
        <div class="p-4 rounded-2xl bg-indigo-50 dark:bg-indigo-500/10">
          <app-icon-v2 name="clock" [size]="28" class="text-indigo-400"></app-icon-v2>
        </div>
        <p class="text-sm font-medium text-gray-700 dark:text-gray-300">No schedules configured</p>
        <p class="text-xs text-gray-400 dark:text-gray-500 max-w-xs">
          Add a schedule to a workflow node via the Workflow tab → node settings → Schedule section.
        </p>
      </div>
    } @else {
      <div class="space-y-3">
        @for (s of schedules(); track s.pid) {
          <div class="rounded-xl border border-gray-200 dark:border-gray-700/60 bg-white dark:bg-gray-900/60 p-4 shadow-sm hover:shadow-md transition-shadow">
            <div class="flex items-start justify-between gap-3">
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-1">
                  <span class="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">{{ s.name }}</span>
                  <span [class]="statusClass(s.lastRunStatus ?? '')" class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide">
                    @if (s.lastRunStatus) { {{ s.lastRunStatus }} } @else { Never run }
                  </span>
                </div>
                <p class="text-xs text-gray-500 dark:text-gray-400 truncate mb-2">{{ s.triggerPrompt }}</p>
                <div class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400 dark:text-gray-500">
                  <span class="flex items-center gap-1">
                    <app-icon-v2 name="terminal" [size]="11"></app-icon-v2>
                    <code class="font-mono">{{ s.cronExpr }}</code>
                  </span>
                  <span class="flex items-center gap-1">
                    <app-icon-v2 name="globe" [size]="11"></app-icon-v2>
                    {{ s.timezone }}
                  </span>
                  <span class="flex items-center gap-1">
                    <app-icon-v2 name="play" [size]="11"></app-icon-v2>
                    {{ s.runCount }} runs · {{ s.successCount }} ok
                  </span>
                  @if (s.lastRunAt) {
                    <span class="flex items-center gap-1">
                      <app-icon-v2 name="clock" [size]="11"></app-icon-v2>
                      Last: {{ formatDate(s.lastRunAt) }}
                    </span>
                  }
                  @if (s.avgDurationMs) {
                    <span class="flex items-center gap-1">
                      <app-icon-v2 name="zap" [size]="11"></app-icon-v2>
                      Avg {{ formatDuration(s.avgDurationMs) }}
                    </span>
                  }
                </div>
              </div>

              <div class="shrink-0 flex items-center gap-2">
                <!-- Delete -->
                <button
                  type="button"
                  (click)="deleteSchedule(s)"
                  [disabled]="togglingId() === s.pid"
                  class="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 text-gray-400 hover:text-red-600 transition-all cursor-pointer disabled:opacity-50"
                  title="Delete schedule (soft delete)"
                  aria-label="Delete schedule"
                >
                  <app-icon-v2 name="trash-2" [size]="14"></app-icon-v2>
                </button>

                <!-- Toggle -->
                <button
                  type="button"
                  (click)="toggleActive(s)"
                  [disabled]="togglingId() === s.pid"
                  class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/50 disabled:opacity-50"
                  [class]="s.isActive ? 'bg-indigo-600 dark:bg-indigo-500' : 'bg-gray-200 dark:bg-gray-700'"
                  [attr.aria-label]="s.isActive ? 'Disable schedule' : 'Enable schedule'"
                >
                  <span
                    class="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform"
                    [class]="s.isActive ? 'translate-x-4' : 'translate-x-0.5'"
                  ></span>
                </button>
              </div>
            </div>
          </div>
        }
      </div>
    }
  }

  <!-- ── RUN LOGS TAB ── -->
  @if (activeTab() === 'logs') {
    @if (logsLoading() && !runLogs().length) {
      <div class="flex items-center justify-center py-16 text-gray-400 dark:text-gray-600">
        <app-icon-v2 name="refresh-ccw" [size]="20" class="animate-spin mr-2"></app-icon-v2>
        Loading run logs…
      </div>
    } @else if (!runLogs().length) {
      <div class="flex flex-col items-center justify-center py-16 gap-3 text-center">
        <div class="p-4 rounded-2xl bg-gray-50 dark:bg-gray-800">
          <app-icon-v2 name="list" [size]="28" class="text-gray-400"></app-icon-v2>
        </div>
        <p class="text-sm font-medium text-gray-700 dark:text-gray-300">No runs yet</p>
        <p class="text-xs text-gray-400 dark:text-gray-500">Run logs will appear here after schedules fire.</p>
      </div>
    } @else {
      <div class="rounded-xl border border-gray-200 dark:border-gray-700/60 overflow-hidden shadow-sm">
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700/60">
              <th class="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Fired At</th>
              <th class="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
              <th class="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Agents</th>
              <th class="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Duration</th>
              <th class="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Tokens</th>
              <th class="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Output</th>
              <th class="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700/40">
            @for (log of runLogs(); track log.pid) {
              <tr class="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                <td class="px-4 py-3 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
                  {{ formatDate(log.firedAt) }}
                  @if (log.isRetry) {
                    <span class="ml-1 text-[10px] text-orange-500 font-medium">(retry #{{ log.attemptNumber }})</span>
                  }
                </td>
                <td class="px-4 py-3">
                  <span [class]="statusClass(log.status)" class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase">
                    <span class="h-1.5 w-1.5 rounded-full shrink-0" [class]="statusDot(log.status)"></span>
                    {{ log.status }}
                  </span>
                  @if (log.usedCachedUpstream) {
                    <span
                      class="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 ring-1 ring-amber-200 dark:ring-amber-500/20"
                    >
                      CACHE
                    </span>
                  }
                </td>
                <td class="px-4 py-3">
                  <div class="flex flex-wrap gap-1.5 max-w-[280px]">
                    @for (agent of getAgentsList(log); track agent.name; let i = $index) {
                      @if (expandedLogIds().includes(log.pid) || i < 2) {
                        <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-50/50 dark:bg-indigo-500/5 text-[10px] font-medium text-indigo-600 dark:text-indigo-400 border border-indigo-100/50 dark:border-indigo-500/10 hover:bg-indigo-50 dark:hover:bg-white/5 transition-colors">
                          {{ agent.name }}
                          @if (agent.toolCount > 0) {
                            <span class="flex items-center gap-0.5 px-1 rounded bg-indigo-100 dark:bg-indigo-500/20 text-[9px] font-bold">
                              <app-icon-v2 name="tool" [size]="8"></app-icon-v2>
                              {{ agent.toolCount }}
                            </span>
                          }
                        </span>
                      }
                    } @empty {
                      <span class="text-gray-400 text-xs italic">No agents recorded</span>
                    }
                    @if (getAgentsList(log).length > 2) {
                      @if (!expandedLogIds().includes(log.pid)) {
                        <button
                          (click)="toggleAgentsExpand(log.pid)"
                          class="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700/60 text-[10px] font-semibold text-gray-500 dark:text-gray-400 border border-gray-200/60 dark:border-gray-600/40 hover:bg-indigo-50 dark:hover:bg-white/10 hover:text-indigo-600 dark:hover:text-white transition-colors cursor-pointer"
                        >
                          +{{ getAgentsList(log).length - 2 }} more
                        </button>
                      } @else {
                        <button
                          (click)="toggleAgentsExpand(log.pid)"
                          class="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700/60 text-[10px] font-semibold text-gray-500 dark:text-gray-400 border border-gray-200/60 dark:border-gray-600/40 hover:bg-indigo-50 dark:hover:bg-white/10 hover:text-indigo-600 dark:hover:text-white transition-colors cursor-pointer"
                        >
                          Show less
                        </button>
                      }
                    }
                  </div>
                </td>
                <td class="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 tabular-nums">{{ formatDuration(log.durationMs) }}</td>
                <td class="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                  @if (log.tokenUsage?.totalTokens) {
                    {{ formatTokens(log.tokenUsage!.totalTokens) }}
                  } @else { — }
                </td>
                <td
                  class="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 max-w-[200px] truncate group cursor-pointer border-b border-transparent hover:border-indigo-500 transition-all font-sans"
                  (click)="$event.stopPropagation(); openDetailsModal('Full Result Output', log.output, 'text', $event)"
                >
                  @if (log.status === 'FAILED' && log.errorMessage) {
                    <span class="text-red-500">{{ log.errorMessage | slice:0:80 }}</span>
                  } @else {
                    <span class="group-hover:text-indigo-600 transition-colors">{{ (log.output ?? '') | slice:0:80 }}{{ log.output && log.output.length > 80 ? '…' : '' }}</span>
                    <app-icon-v2 name="eye" [size]="10" class="ml-1 opacity-0 group-hover:opacity-100 text-indigo-500 transition-all"></app-icon-v2>
                  }
                </td>
                <td class="px-4 py-3">
                  @if (log.traceId) {
                    <button
                      (click)="openDagView(log)"
                      class="text-xs text-indigo-600 dark:text-indigo-400 hover:underline font-medium whitespace-nowrap cursor-pointer"
                    >
                      Detail →
                    </button>
                  }
                </td>
              </tr>
            }
          </tbody>
        </table>

        <!-- Pagination bar -->
        @if (logsTotal() > 0) {
          <div class="border-t border-gray-100 dark:border-gray-700/40 bg-gray-50/40 dark:bg-gray-800/30">
            @if (logsPagination()) {
              <div class="px-4 pt-2 text-[11px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 flex gap-3">
                <span>Page tokens {{ formatTokens(logsPageTokensUsed()) }}</span>
                <span>| Total tokens {{ formatTokens(logsTotalTokensUsed()) }}</span>
              </div>
            }
            <app-pagination
              [currentPage]="currentPage()"
              [totalItems]="logsTotal()"
              [pageSize]="logsPageSize"
              paddingXAxis="px-4"
              paddingYAxis="py-2"
              (pageChange)="onPageChange($event)"
            />
          </div>
        }
      </div>
    }
  }

  <!-- ── AGENT BREAKDOWN TAB ── -->
  @if (activeTab() === 'dag') {
    <div class="space-y-4">
      <!-- Back + context header -->
      <div class="flex items-center gap-3">
        <button
          (click)="backToLogs()"
          class="flex items-center gap-1.5 text-xs text-purple-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors cursor-pointer"
        >
          <app-icon-v2 name="arrow-left" [size]="13"></app-icon-v2>
          Back to logs
        </button>
        @if (selectedLogForDag(); as log) {
          <span class="text-xs text-gray-400 dark:text-gray-500">
            Run at {{ formatDate(log.firedAt) }} ·
            <span [class]="statusClass(log.status)" class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase">
              {{ log.status }}
            </span>
          </span>
        }
      </div>

      @if (dagLoading()) {
        <div class="flex items-center justify-center py-12 text-gray-400 dark:text-gray-600">
          <app-icon-v2 name="refresh-ccw" [size]="18" class="animate-spin mr-2"></app-icon-v2>
          Loading agent executions…
        </div>
      } @else if (!dagExecutions().length) {
        <div class="flex flex-col items-center justify-center py-12 gap-2">
          <app-icon-v2 name="git-branch" [size]="28" class="text-gray-300 dark:text-gray-600"></app-icon-v2>
          <p class="text-sm text-gray-400 dark:text-gray-500">No agent execution data found.</p>
        </div>
      } @else {
        <!-- Token summary bar -->
        <div class="flex items-center gap-6 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60 px-5 py-3 text-sm">
          <div>
            <span class="text-xs text-gray-400 uppercase tracking-wide">Nodes</span>
            <p class="font-bold text-gray-800 dark:text-gray-100">{{ dagExecutions().length }}</p>
          </div>
          <div>
            <span class="text-xs text-gray-400 uppercase tracking-wide">Total Tokens</span>
            <p class="font-bold text-purple-600 dark:text-purple-400">
              {{ formatTokens(dagExecutions().reduce(toTokenSum, 0)) }}
            </p>
          </div>
          <div>
            <span class="text-xs text-gray-400 uppercase tracking-wide">Total Duration</span>
            <p class="font-bold text-indigo-600 dark:text-indigo-400">
              {{ formatDuration(dagExecutions().reduce(toDurationSum, 0)) }}
            </p>
          </div>
          @if (selectedLogForDag()?.usedCachedUpstream) {
            <div>
              <span class="text-xs text-gray-400 uppercase tracking-wide">Cache</span>
              <p class="font-bold text-amber-700 dark:text-amber-300">Used</p>
            </div>
          } @else {
            <div>
              <span class="text-xs text-gray-400 uppercase tracking-wide">Stale Nodes</span>
              <p class="font-bold text-amber-600 dark:text-amber-400">
                {{ dagExecutions().filter(isStaleNode).length }}
              </p>
            </div>
          }
        </div>

        <!-- Node waterfall -->
        <div class="space-y-2">
          @for (node of dagExecutions(); track node.pid) {
            <div class="rounded-xl border bg-white dark:bg-gray-900/60 p-4 shadow-sm transition-all"
              [class]="node.status === 'FAILED'
                ? 'border-red-200 dark:border-red-500/20'
                : node.isStale
                  ? 'border-amber-200 dark:border-amber-500/20'
                  : 'border-gray-200 dark:border-gray-700/60'"
            >
              <div class="flex items-center gap-3">
                <!-- Order badge -->
                <span class="shrink-0 h-7 w-7 rounded-full bg-indigo-100 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 text-xs font-bold flex items-center justify-center">
                  {{ node.executionOrder + 1 }}
                </span>

                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-0.5">
                    <span class="font-semibold text-sm text-gray-900 dark:text-gray-100">{{ node.nodeName }}</span>
                    <span [class]="statusClass(node.status)" class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase">
                      <span class="h-1.5 w-1.5 rounded-full" [class]="statusDot(node.status)"></span>
                      {{ node.status }}
                    </span>
                    @if (node.isStale) {
                      <span class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 ring-1 ring-amber-200 dark:ring-amber-500/20">
                        STALE
                      </span>
                    }
                    @if (node.retryCount > 0) {
                      <span class="text-[10px] text-orange-500 dark:text-orange-400 font-medium">
                        {{ node.retryCount }} retries
                      </span>
                    }
                  </div>

                  <div class="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-400 dark:text-gray-500">
                    <span class="tabular-nums">{{ formatDuration(node.durationMs) }}</span>
                    @if (node.totalTokens) {
                      <span>{{ formatTokens(node.inputTokens) }} in · {{ formatTokens(node.outputTokens) }} out · {{ formatTokens(node.totalTokens) }} total</span>
                    }
                    @if (node.errorCode) {
                      <span class="text-red-500 dark:text-red-400 font-medium">{{ node.errorCode }}</span>
                    }
                    <span class="text-gray-300 dark:text-gray-600">{{ formatTime(node.startedAt) }}</span>
                  </div>

                  @if (node.status === 'FAILED' && node.errorMessage) {
                    <p class="mt-1.5 text-xs text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-500/5 rounded-lg px-2 py-1.5 font-mono">
                      {{ node.errorMessage | slice:0:200 }}
                    </p>
                  }
                </div>

                <!-- Node Actions -->
                <div class="shrink-0 flex items-center gap-2">
                  <div class="text-right mr-2">
                    <p class="text-xs font-mono text-gray-400 dark:text-gray-500">{{ formatDuration(node.durationMs) }}</p>
                  </div>
                  <button
                    (click)="openDetailsModal('Execution Details: ' + node.nodeName, node, 'json', $event)"
                    class="p-1.5 rounded-lg hover:bg-indigo-50 dark:hover:bg-white/10 text-gray-400 hover:text-indigo-600 transition-all cursor-pointer"
                    title="View Input/Output Payloads"
                  >
                    <app-icon-v2 name="maximize2" [size]="14"></app-icon-v2>
                  </button>
                </div>
              </div>
            </div>
          }
        </div>
      }
    </div>
  }

  <!-- ── DELIVERY SETTINGS TAB ── -->
  @if (activeTab() === 'delivery') {
    <div class="space-y-5 animate-in fade-in duration-300">

      @if (deliveryLoading()) {
        <div class="flex items-center justify-center py-16 text-gray-400 dark:text-gray-600">
          <app-icon-v2 name="refresh-ccw" [size]="18" class="animate-spin mr-2"></app-icon-v2>
          Loading delivery config…
        </div>
      } @else {

        <!-- Description -->
        <div class="flex items-start justify-between gap-3">
          <p class="text-xs text-gray-500 dark:text-gray-400">
            Configure where completed schedule reports are delivered.
            If no channel is enabled the system falls back to the default Google Chat channel.
          </p>

          <div class="shrink-0 flex items-center gap-2">
            @if (deliveryDirty()) {
              <span class="text-[11px] font-semibold text-amber-600 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 px-2 py-1 rounded-lg">
                Unsaved changes
              </span>
            } @else {
              <span class="text-[11px] font-semibold text-emerald-600 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 px-2 py-1 rounded-lg">
                Saved
              </span>
            }
            <button
              type="button"
              (click)="saveDeliveryAll()"
              [disabled]="deliverySaving() || !deliveryDirty()"
              class="flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm active:scale-[0.98]"
              title="Save delivery settings"
            >
              @if (deliverySaving()) {
                <app-icon-v2 name="refresh-ccw" [size]="13" class="animate-spin"></app-icon-v2>
                Saving…
              } @else {
                <app-icon-v2 name="save" [size]="13"></app-icon-v2>
                Save changes
              }
            </button>
          </div>
        </div>

        <!-- ── Cards: side by side ──────────────────────────────────── -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">

          <!-- Email Reports card -->
          <div class="rounded-xl border border-gray-200 dark:border-gray-700/60 bg-white dark:bg-gray-900/60 p-5 shadow-sm space-y-4 flex flex-col">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2.5">
                <div class="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-500/10">
                  <app-icon-v2 name="mail" [size]="16" class="text-indigo-500"></app-icon-v2>
                </div>
                <div>
                  <p class="text-sm font-semibold text-gray-900 dark:text-gray-100">Email Reports</p>
                  <p class="text-xs text-gray-400 dark:text-gray-500">Reports will be sent to this address</p>
                </div>
              </div>

              <!-- Toggle -->
              <button
                type="button"
                (click)="emailEnabled.set(!emailEnabled())"
                class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none"
                [class]="emailEnabled() ? 'bg-indigo-600 dark:bg-indigo-500' : 'bg-gray-200 dark:bg-gray-700'"
              >
                <span
                  class="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform"
                  [class]="emailEnabled() ? 'translate-x-4' : 'translate-x-0.5'"
                ></span>
              </button>
            </div>

            <div class="space-y-2 flex-1">
              <label class="text-xs font-medium text-gray-600 dark:text-gray-300">Recipient Email Address</label>
              <input
                type="text"
                [(ngModel)]="emailRecipients"
                [ngModel]="emailRecipients()"
                (ngModelChange)="emailRecipients.set($event)"
                placeholder="user@example.com, another@example.com"
                class="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400 transition-all"
              />
              <p class="text-[11px] text-gray-400 dark:text-gray-500">Separate multiple addresses with commas.</p>
            </div>

            <div class="h-1"></div>
          </div>

          <!-- Google Chat card -->
          <div class="rounded-xl border border-gray-200 dark:border-gray-700/60 bg-white dark:bg-gray-900/60 p-5 shadow-sm space-y-4 flex flex-col">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2.5">
                <div class="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/10">
                  <app-icon-v2 name="message-circle" [size]="16" class="text-emerald-500"></app-icon-v2>
                </div>
                <div>
                  <p class="text-sm font-semibold text-gray-900 dark:text-gray-100">Google Chat</p>
                  <p class="text-xs text-gray-400 dark:text-gray-500">Post reports to a Space</p>
                </div>
              </div>

              <!-- Toggle -->
              <button
                type="button"
                (click)="googleChatEnabled.set(!googleChatEnabled())"
                class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none"
                [class]="googleChatEnabled() ? 'bg-emerald-500' : 'bg-gray-200 dark:bg-gray-700'"
              >
                <span
                  class="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform"
                  [class]="googleChatEnabled() ? 'translate-x-4' : 'translate-x-0.5'"
                ></span>
              </button>
            </div>

            <div class="space-y-2 flex-1">
              <label class="text-xs font-medium text-gray-600 dark:text-gray-300">Webhook URL</label>
              <input
                type="url"
                [ngModel]="googleChatWebhookUrl()"
                (ngModelChange)="googleChatWebhookUrl.set($event)"
                placeholder="https://chat.googleapis.com/v1/spaces/..."
                class="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400 transition-all"
              />
              <p class="text-[11px] text-gray-400 dark:text-gray-500">
                To get a webhook URL, open Google Chat, go to Space Settings → Apps &amp; Integrations → Webhooks.
              </p>
            </div>

            <div class="h-1"></div>
          </div>

        </div>

        <!-- Fallback notice -->
        @if (!emailEnabled() && !googleChatEnabled()) {
          <div class="flex items-start gap-2.5 px-4 py-3 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 text-amber-700 dark:text-amber-300">
            <app-icon-v2 name="alert-triangle" [size]="14" class="mt-0.5 shrink-0"></app-icon-v2>
            <p class="text-xs leading-relaxed">
              No delivery channel is enabled. Reports will fall back to the <strong>default Google Chat channel</strong>.
            </p>
          </div>
        }
      }
    </div>
  }

  <!-- Last refreshed -->
  @if (lastRefreshed()) {
    <p class="text-[11px] text-gray-300 dark:text-gray-600 text-right pt-2">
      Last refreshed {{ formatTime(lastRefreshed()!.toISOString()) }} · auto-refreshes every 1m
    </p>
  }

</div>

<!-- Details Modal Overlay -->
@if (detailsModalOpen()) {
  <div class="fixed inset-0 z-[100] flex items-center justify-center p-4">
    <!-- Backdrop -->
    <div
      class="absolute inset-0 bg-gray-950/50 backdrop-blur-sm animate-in fade-in duration-300"
      (click)="closeModal()"
    ></div>

    <!-- Modal Card -->
    <div class="relative w-full max-w-4xl max-h-[90vh] flex flex-col bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden animate-in zoom-in-95 duration-300">
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800">
        <h3 class="text-lg font-bold text-gray-900 dark:text-white">{{ modalTitle() }}</h3>
        <button
          (click)="closeModal()"
          class="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition-colors"
        >
          <app-icon-v2 name="x" [size]="18"></app-icon-v2>
        </button>
      </div>

      <!-- Body -->
      <div class="flex-1 overflow-auto p-6 scrollbar-thin">
        @if (modalType() === 'json') {
          <div class="flex flex-col h-full">
            <!-- Modal Tabs -->
            <div class="flex items-center gap-1 border-b border-gray-100 dark:border-gray-800 mb-6 -mx-6 px-6">
              @if (modalContent()?.outputResponse) {
                <button
                  (click)="detailsTab.set('output')"
                  [class]="detailsTab() === 'output'
                    ? 'border-b-2 border-emerald-500 text-emerald-600 dark:text-emerald-400 font-semibold'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'"
                  class="px-4 py-2.5 text-xs transition-colors -mb-px flex items-center gap-1.5"
                >
                  <app-icon-v2 name="message-square" [size]="12"></app-icon-v2>
                  Output Response
                </button>
              }
              @if (modalContent()?.inputPayload) {
                <button
                  (click)="detailsTab.set('input')"
                  [class]="detailsTab() === 'input'
                    ? 'border-b-2 border-indigo-500 text-indigo-600 dark:text-indigo-400 font-semibold'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'"
                  class="px-4 py-2.5 text-xs transition-colors -mb-px flex items-center gap-1.5"
                >
                  <app-icon-v2 name="terminal" [size]="12"></app-icon-v2>
                  Input Payload
                </button>
              }
              @if (modalContent()?.toolResults && modalContent()?.toolResults.length) {
                <button
                  (click)="detailsTab.set('tools')"
                  [class]="detailsTab() === 'tools'
                    ? 'border-b-2 border-purple-500 text-purple-600 dark:text-purple-400 font-semibold'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'"
                  class="px-4 py-2.5 text-xs transition-colors -mb-px flex items-center gap-1.5"
                >
                  <app-icon-v2 name="tool" [size]="12"></app-icon-v2>
                  Tool Results
                </button>
              }
            </div>

            <!-- Tab Content -->
            <div class="animate-in fade-in slide-in-from-bottom-2 duration-300">
              @if (detailsTab() === 'input' && modalContent()?.inputPayload) {
                <div>
                  <h4 class="text-[10px] font-bold text-indigo-500 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                    <app-icon-v2 name="terminal" [size]="12"></app-icon-v2>
                    Input Payload (Sent to Agent)
                  </h4>
                  <pre class="bg-gray-50 dark:bg-gray-950/80 p-5 rounded-2xl text-xs font-mono text-gray-700 dark:text-gray-300 overflow-x-auto border border-gray-100 dark:border-gray-800 scrollbar-thin">{{ modalContent().inputPayload.query }}</pre>
                </div>
              }

              @if (detailsTab() === 'output' && modalContent()?.outputResponse) {
                <div>
                  <h4 class="text-[10px] font-bold text-emerald-500 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                    <app-icon-v2 name="message-square" [size]="12"></app-icon-v2>
                    Output Response (Agent Answer)
                  </h4>
                  <div class="bg-gray-50 dark:bg-gray-950/80 p-5 rounded-2xl text-sm leading-relaxed text-gray-700 dark:text-gray-300 border border-gray-100 dark:border-gray-800 whitespace-pre-wrap font-sans">
                    {{ modalContent().outputResponse }}
                  </div>
                </div>
              }

              @if (detailsTab() === 'tools' && modalContent()?.toolResults && modalContent()?.toolResults.length) {
                <div>
                  <h4 class="text-[10px] font-bold text-purple-500 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                    <app-icon-v2 name="tool" [size]="12"></app-icon-v2>
                    Raw Tool Execution Results
                  </h4>
                  <pre class="bg-gray-50 dark:bg-gray-950/80 p-5 rounded-2xl text-xs font-mono text-gray-700 dark:text-gray-300 overflow-x-auto border border-gray-100 dark:border-gray-800 scrollbar-thin">{{ prettyJson(modalContent().toolResults) }}</pre>
                </div>
              }
            </div>
          </div>
        } @else {
          <div class="text-sm leading-relaxed text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-sans bg-gray-50 dark:bg-gray-950/50 p-6 rounded-2xl border border-gray-100 dark:border-gray-800">
            {{ modalContent() }}
          </div>
        }
      </div>

      <!-- Footer -->
      <div class="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800 flex justify-end">
        <button
          (click)="closeModal()"
          class="px-6 py-2 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-semibold hover:opacity-90 transition-all shadow-md active:scale-95"
        >
          Close Preview
        </button>
      </div>
    </div>
  </div>
}
